import { useState, useCallback, useEffect, useRef } from "react"
import {
  SECTIONS, ALL_FIELDS, buildFormState, FieldDef, SectionDef,
  PREFERRED_HOSPITAL_FIELD, PREFERRED_PHARMACY_FIELD,
} from "../fieldConfig"

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL!

const PRIMARY_SECTION       = SECTIONS.find(s => s.id === "primary")!
const ADDRESS_SECTION       = SECTIONS.find(s => s.id === "address")!
const SPOUSE_SECTION        = SECTIONS.find(s => s.id === "spouse")!
const DEP_SECTIONS          = SECTIONS.filter(s => s.id.startsWith("dep"))
const POLICY_SECTIONS       = SECTIONS.filter(s => s.id.startsWith("policy") && !s.id.startsWith("pastPolicy"))
const DOCTOR_SECTIONS       = SECTIONS.filter(s => s.id.startsWith("doc"))
const MED_SECTIONS          = SECTIONS.filter(s => s.id.startsWith("med"))
const PAST_POLICY_SECTIONS  = SECTIONS.filter(s => s.id.startsWith("pastPolicy"))

type CardType = "primary" | "spouse" | "dep" | "policy" | "past" | "address" | "doctor" | "medication"

function getCardType(id: string): CardType {
  if (id === "primary")            return "primary"
  if (id === "spouse")             return "spouse"
  if (id === "address")            return "address"
  if (id.startsWith("pastPolicy")) return "past"
  if (id.startsWith("policy"))     return "policy"
  if (id.startsWith("doc"))        return "doctor"
  if (id.startsWith("med"))        return "medication"
  return "dep"
}

const CARD_EMOJI: Record<CardType, string> = {
  primary:    "👤",
  spouse:     "👥",
  dep:        "👤",
  policy:     "📋",
  past:       "📁",
  address:    "🏠",
  doctor:     "🏥",
  medication: "💊",
}

const GROUP_EMOJI: Record<string, string> = {
  deps:        "👤",
  active:      "📋",
  doctors:     "🏥",
  medications: "💊",
  past:        "📁",
}

// Keys suffix-matched to p${n}${key} for active policy shift
const ACTIVE_KEYS = ["Carrier", "Product", "Number", "LineOfBusiness", "Premium", "AppDate", "EffDate"] as const
// Keys suffix-matched to pp${n}${key} for past policy shift
const PAST_KEYS   = ["CarrierProduct", "LineOfBusiness", "EffCancelDate", "ArchivedDate"] as const

function fmtDate(iso: string): string {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso
  const [y, m, d] = iso.split("-")
  return `${m}-${d}-${y}`
}

function buildShiftUpdates(
  sections: SectionDef[],
  deleteIdx: number,
  form: Record<string, string>
): Record<string, string> {
  const updates: Record<string, string> = {}
  const last = sections.length - 1
  for (let i = deleteIdx; i < last; i++) {
    const src = sections[i + 1]
    const dst = sections[i]
    for (let j = 0; j < dst.fields.length; j++) {
      updates[dst.fields[j].key] = form[src.fields[j].key] ?? ""
    }
  }
  for (const field of sections[last].fields) {
    updates[field.key] = ""
  }
  return updates
}

function buildMoveUpdates(
  sections: SectionDef[],
  fromIdx: number,
  toIdx: number,
  form: Record<string, string>
): Record<string, string> {
  if (fromIdx === toIdx) return {}
  const updates: Record<string, string> = {}
  // Store source values by position so they can be written into the destination
  // section's field keys (which differ from the source section's keys).
  const srcValues = sections[fromIdx].fields.map(f => form[f.key] ?? "")
  if (fromIdx < toIdx) {
    for (let i = fromIdx; i < toIdx; i++) {
      const src = sections[i + 1], dst = sections[i]
      for (let j = 0; j < dst.fields.length; j++)
        updates[dst.fields[j].key] = form[src.fields[j].key] ?? ""
    }
  } else {
    for (let i = fromIdx; i > toIdx; i--) {
      const src = sections[i - 1], dst = sections[i]
      for (let j = 0; j < dst.fields.length; j++)
        updates[dst.fields[j].key] = form[src.fields[j].key] ?? ""
    }
  }
  const dstFields = sections[toIdx].fields
  for (let j = 0; j < dstFields.length; j++) updates[dstFields[j].key] = srcValues[j] ?? ""
  return updates
}

function applyDragPreview(
  visible: SectionDef[],
  full: SectionDef[],
  drag: { group: string; from: number; to: number } | null,
  group: string
): SectionDef[] {
  if (!drag || drag.group !== group || drag.from === drag.to) return visible
  const dragged = full[drag.from]
  const target  = full[drag.to]
  const fi = visible.indexOf(dragged)
  const ti = visible.indexOf(target)
  if (fi === -1 || ti === -1) return visible
  const out = [...visible]
  out.splice(fi, 1)
  out.splice(ti, 0, dragged)
  return out
}

type FieldSaveState = 'idle' | 'saving' | 'saved'

type PolicyDialogState = {
  type: "delete" | "archive"
  sectionId: string
  label: string
  summary: [string, string][]
}

interface Props {
  contact: any
  sessionToken: string
  onUpdate: (updated: any) => void
  devMode?: boolean
  autosave: boolean
  onAutosaveToggle: (v: boolean) => void
}

export default function ContactForm({ contact, sessionToken, onUpdate, devMode, autosave, onAutosaveToggle }: Props) {
  const [form, setForm]             = useState<Record<string, string>>(() => buildFormState(contact))
  const [saving, setSaving]         = useState(false)
  const [saved, setSaved]           = useState(false)
  const [error, setError]           = useState<string | null>(null)
  const [dialogState, setDialogState] = useState<PolicyDialogState | null>(null)

  const [depsOpen, setDepsOpen] = useState(() => {
    const initial = buildFormState(contact)
    return DEP_SECTIONS.some(s => s.fields.some(f => !f.isGender && initial[f.key] !== ""))
  })
  const [policiesOpen, setPoliciesOpen] = useState(() => {
    const initial = buildFormState(contact)
    return POLICY_SECTIONS.some(s => s.fields.some(f => initial[f.key] !== ""))
  })
  const [pastPoliciesOpen, setPastPoliciesOpen] = useState(false)
  const [showAllDeps, setShowAllDeps]           = useState(false)
  const [showAllPolicies, setShowAllPolicies]   = useState(false)
  const [showAllPast, setShowAllPast]           = useState(false)
  const [doctorsOpen, setDoctorsOpen]           = useState(false)
  const [medicationsOpen, setMedicationsOpen]   = useState(false)
  const [showAllDoctors, setShowAllDoctors]     = useState(false)
  const [showAllMeds, setShowAllMeds]           = useState(false)
  const [fieldStates, setFieldStates] = useState<Record<string, FieldSaveState>>({})
  const [dragState, setDragState] = useState<{ group: string; from: number; to: number } | null>(null)

  // Close dialog on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setDialogState(null) }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [])

  const dirtyFields = useRef(new Set<string>())

  const handleChange = useCallback((key: string, value: string) => {
    setForm(prev => {
      const next = { ...prev, [key]: value }
      const qMatch = key.match(/^med(\d+)Quantity$/)
      if (qMatch && value && !prev[`med${qMatch[1]}Frequency`]) {
        const freqKey = `med${qMatch[1]}Frequency`
        next[freqKey] = "Every Month"
        dirtyFields.current.add(freqKey)
      }
      return next
    })
    dirtyFields.current.add(key)
    setSaved(false)
    setError(null)
  }, [])

  const handleFieldBlur = useCallback(async (key: string, value: string) => {
    if (!dirtyFields.current.has(key)) return
    dirtyFields.current.delete(key)

    const field = ALL_FIELDS.find(f => f.key === key)
    if (!field) return

    if (devMode) {
      setFieldStates(prev => ({ ...prev, [key]: 'saving' as FieldSaveState }))
      await new Promise(r => setTimeout(r, 400))
      setFieldStates(prev => ({ ...prev, [key]: 'saved' as FieldSaveState }))
      return
    }

    setFieldStates(prev => ({ ...prev, [key]: 'saving' as FieldSaveState }))
    const standardFields: Record<string, string> = {}
    const customUpdates: Record<string, string>  = {}
    if (field.standard)       { if (value !== "") standardFields[field.standard]  = value }
    else if (field.customKey) { if (value !== "") customUpdates[field.customKey]  = value }

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/update-contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionToken, standardFields, customUpdates }),
      })
      const next: FieldSaveState = res.ok ? 'saved' : 'idle'
      setFieldStates(prev => ({ ...prev, [key]: next }))
    } catch {
      setFieldStates(prev => ({ ...prev, [key]: 'idle' as FieldSaveState }))
    }
  }, [sessionToken, devMode])

  // ── Drag-to-reorder (mouse events, no HTML5 DnD) ──────────────────────────
  function groupSections(group: string): SectionDef[] {
    if (group === 'dep')    return DEP_SECTIONS
    if (group === 'policy') return POLICY_SECTIONS
    if (group === 'past')   return PAST_POLICY_SECTIONS
    if (group === 'doc')    return DOCTOR_SECTIONS
    if (group === 'med')    return MED_SECTIONS
    return []
  }

  const formRef = useRef(form)
  formRef.current = form

  function handleGripMouseDown(e: React.MouseEvent, group: string, fromIdx: number) {
    e.preventDefault()
    let currentTo = fromIdx
    let dragStarted = false
    const startX = e.clientX
    const startY = e.clientY
    document.body.style.cursor = 'grabbing'
    document.body.style.userSelect = 'none'

    function onMouseMove(me: MouseEvent) {
      if (!dragStarted) {
        const dx = me.clientX - startX
        const dy = me.clientY - startY
        if (dx * dx + dy * dy < 25) return
        dragStarted = true
        setDragState({ group, from: fromIdx, to: fromIdx })
      }
      const el = document.elementFromPoint(me.clientX, me.clientY)
      const cardEl = (el as HTMLElement | null)?.closest('[data-drag-group]') as HTMLElement | null
      if (cardEl?.dataset.dragGroup === group) {
        const newTo = parseInt(cardEl.dataset.dragIdx ?? '-1')
        if (newTo !== -1 && newTo !== currentTo) {
          currentTo = newTo
          setDragState({ group, from: fromIdx, to: newTo })
        }
      } else if (dragStarted && currentTo !== fromIdx) {
        // Mouse is over a non-card area (including the pointer-events:none dragging card)
        // — snap target back to source so it can be dropped in place
        currentTo = fromIdx
        setDragState({ group, from: fromIdx, to: fromIdx })
      }
    }

    async function onMouseUp() {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      setDragState(null)
      if (currentTo !== fromIdx) {
        const updates = buildMoveUpdates(groupSections(group), fromIdx, currentTo, formRef.current)
        setForm(prev => ({ ...prev, ...updates }))
        if (autosave) await saveFieldsMap(updates)
      }
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  // ── Targeted save (used by delete/archive — sends empty strings to clear fields) ──
  async function saveFieldsMap(updates: Record<string, string>): Promise<void> {
    if (devMode) return
    const standardFields: Record<string, string> = {}
    const customUpdates: Record<string, string>  = {}
    for (const field of ALL_FIELDS) {
      if (!(field.key in updates)) continue
      const val = updates[field.key]
      if (field.standard)   standardFields[field.standard]  = val
      if (field.customKey)  customUpdates[field.customKey]  = val
    }
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/update-contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionToken, standardFields, customUpdates }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        setError(err.error ?? "Failed to save changes.")
      } else {
        setSaved(true)
      }
    } catch {
      setError("Network error — please try again.")
    }
  }

  // ── Policy dialog helpers ──────────────────────────────────────────────────
  function policyNum(sectionId: string): number {
    if (sectionId.startsWith("pastPolicy")) return parseInt(sectionId.replace("pastPolicy", ""), 10)
    return parseInt(sectionId.replace("policy", ""), 10)
  }

  function openDeleteDialog(sectionId: string) {
    const section = SECTIONS.find(s => s.id === sectionId)!

    if (sectionId.startsWith("dep")) {
      const n = parseInt(sectionId.replace("dep", ""), 10)
      const summary: [string, string][] = [
        ["First Name", form[`dep${n}First`]              || "—"],
        ["Last Name",  form[`dep${n}Last`]               || "—"],
        ["DOB",        fmtDate(form[`dep${n}Dob`] ?? "") || "—"],
      ]
      setDialogState({ type: "delete", sectionId, label: section.label, summary })
      return
    }
    if (sectionId.startsWith("doc")) {
      const n = parseInt(sectionId.replace("doc", ""), 10)
      const summary: [string, string][] = [
        ["Name",      form[`doc${n}Name`]      || "—"],
        ["Specialty", form[`doc${n}Specialty`] || "—"],
      ]
      setDialogState({ type: "delete", sectionId, label: section.label, summary })
      return
    }
    if (sectionId.startsWith("med")) {
      const n = parseInt(sectionId.replace("med", ""), 10)
      const summary: [string, string][] = [
        ["Name",   form[`med${n}Name`]   || "—"],
        ["Dosage", form[`med${n}Dosage`] || "—"],
      ]
      setDialogState({ type: "delete", sectionId, label: section.label, summary })
      return
    }

    const n = policyNum(sectionId)
    const isActive = !sectionId.startsWith("pastPolicy")

    const summary: [string, string][] = isActive
      ? [
          ["Carrier",   form[`p${n}Carrier`]  || "—"],
          ["Product",   form[`p${n}Product`]  || "—"],
          ["Policy #",  form[`p${n}Number`]   || "—"],
          ["Premium",   form[`p${n}Premium`]  || "—"],
          ["Eff. Date", form[`p${n}EffDate`]  || "—"],
        ]
      : [
          ["Carrier / Product", form[`pp${n}CarrierProduct`] || "—"],
          ["Line of Business",  form[`pp${n}LineOfBusiness`] || "—"],
          ["Eff / Cancel Date", form[`pp${n}EffCancelDate`]  || "—"],
        ]

    setDialogState({ type: "delete", sectionId, label: section.label, summary })
  }

  function openArchiveDialog(sectionId: string) {
    const section = SECTIONS.find(s => s.id === sectionId)!
    const n = policyNum(sectionId)

    const summary: [string, string][] = [
      ["Carrier",          form[`p${n}Carrier`]        || "—"],
      ["Product",          form[`p${n}Product`]        || "—"],
      ["Policy #",         form[`p${n}Number`]         || "—"],
      ["Line of Business", form[`p${n}LineOfBusiness`] || "—"],
      ["Premium",          form[`p${n}Premium`]        || "—"],
      ["Eff. Date",        fmtDate(form[`p${n}EffDate`]) || "—"],
    ]

    setDialogState({ type: "archive", sectionId, label: section.label, summary })
  }

  async function executeConfirm(cancelDate: string = "") {
    if (!dialogState) return
    const { type, sectionId } = dialogState
    setDialogState(null)

    if (type === "delete") {
      if (sectionId.startsWith("dep")) {
        const deleteIdx = parseInt(sectionId.replace("dep", ""), 10) - 1
        const updates = buildShiftUpdates(DEP_SECTIONS, deleteIdx, form)
        setForm(prev => ({ ...prev, ...updates }))
        await saveFieldsMap(updates)
        return
      }
      if (sectionId.startsWith("doc")) {
        const deleteIdx = parseInt(sectionId.replace("doc", ""), 10) - 1
        const updates = buildShiftUpdates(DOCTOR_SECTIONS, deleteIdx, form)
        setForm(prev => ({ ...prev, ...updates }))
        await saveFieldsMap(updates)
        return
      }
      if (sectionId.startsWith("med")) {
        const deleteIdx = parseInt(sectionId.replace("med", ""), 10) - 1
        const updates = buildShiftUpdates(MED_SECTIONS, deleteIdx, form)
        setForm(prev => ({ ...prev, ...updates }))
        await saveFieldsMap(updates)
        return
      }

      const n = policyNum(sectionId)
      const isPast = sectionId.startsWith("pastPolicy")
      const updates: Record<string, string> = {}

      if (isPast) {
        for (let k = n; k <= 7; k++) {
          for (const key of PAST_KEYS) updates[`pp${k}${key}`] = form[`pp${k + 1}${key}`] || ""
        }
        for (const key of PAST_KEYS) updates[`pp8${key}`] = ""
      } else {
        for (let k = n; k <= 7; k++) {
          for (const key of ACTIVE_KEYS) updates[`p${k}${key}`] = form[`p${k + 1}${key}`] || ""
        }
        for (const key of ACTIVE_KEYS) updates[`p8${key}`] = ""
      }

      setForm(prev => ({ ...prev, ...updates }))
      await saveFieldsMap(updates)
      return
    }

    // Archive: move to next available past policy slot, then shift remaining active policies up
    const n = policyNum(sectionId)
    let pastSlot: number | null = null
    for (let m = 1; m <= 8; m++) {
      if (!form[`pp${m}CarrierProduct`]) { pastSlot = m; break }
    }
    if (!pastSlot) {
      setError("No available Past Policy slots (all 8 are filled).")
      return
    }

    const ps = pastSlot
    const carrier = form[`p${n}Carrier`] || ""
    const product = form[`p${n}Product`] || ""
    const lob     = form[`p${n}LineOfBusiness`] || ""
    const effDate = form[`p${n}EffDate`] || ""
    const today   = new Date().toISOString().slice(0, 10)

    const formattedCancel = cancelDate === "N/A" ? "N/A" : fmtDate(cancelDate)
    const effCancelStr = [effDate ? fmtDate(effDate) : "", formattedCancel]
      .filter(Boolean).join(" — ")

    const updates: Record<string, string> = {
      [`pp${ps}CarrierProduct`]: [carrier, product].filter(Boolean).join(" / "),
      [`pp${ps}LineOfBusiness`]: lob,
      [`pp${ps}EffCancelDate`]:  effCancelStr,
      [`pp${ps}ArchivedDate`]:   today,
    }

    // Shift policies n+1 → n, n+2 → n+1, …, 8 → 7, then clear slot 8
    for (let k = n; k <= 7; k++) {
      for (const key of ACTIVE_KEYS) {
        updates[`p${k}${key}`] = form[`p${k + 1}${key}`] || ""
      }
    }
    for (const key of ACTIVE_KEYS) {
      updates[`p8${key}`] = ""
    }

    setForm(prev => ({ ...prev, ...updates }))
    setPastPoliciesOpen(true)
    await saveFieldsMap(updates)
  }

  // ── Full save ──────────────────────────────────────────────────────────────
  async function handleSave() {
    setSaving(true)
    setError(null)

    if (devMode) {
      await new Promise(r => setTimeout(r, 400))
      setSaved(true)
      setSaving(false)
      return
    }

    const standardFields: Record<string, string> = {}
    const customUpdates: Record<string, string>  = {}

    for (const field of ALL_FIELDS) {
      const value = form[field.key]
      if (value === undefined) continue
      if (field.standard)       { if (value !== "") standardFields[field.standard]  = value }
      else if (field.customKey) { if (value !== "") customUpdates[field.customKey]  = value }
    }

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/update-contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionToken, standardFields, customUpdates }),
      })
      if (!response.ok) {
        if (response.status === 403) {
          setError("Session expired — close and reopen.")
        } else {
          const err = await response.json().catch(() => ({}))
          setError(err.error ?? "Failed to save. Please try again.")
        }
        return
      }
      const { contact: updated } = await response.json()
      onUpdate(updated)
      setSaved(true)
    } catch {
      setError("Network error — please try again.")
    } finally {
      setSaving(false)
    }
  }

  const hasDeps         = DEP_SECTIONS.some(s => s.fields.some(f => !f.isGender && (form[f.key] ?? "") !== ""))
  const hasPolicies     = POLICY_SECTIONS.some(s => s.fields.some(f => (form[f.key] ?? "") !== ""))
  const hasDoctors      = DOCTOR_SECTIONS.some(s => s.fields.some(f => (form[f.key] ?? "") !== ""))
  const hasMeds         = MED_SECTIONS.some(s => s.fields.some(f => (form[f.key] ?? "") !== ""))
  const hasPastPolicies = PAST_POLICY_SECTIONS.some(s => s.fields.some(f => (form[f.key] ?? "") !== ""))

  const depsWithData         = DEP_SECTIONS.filter(s => s.fields.some(f => !f.isGender && (form[f.key] ?? "") !== ""))
  const policiesWithData     = POLICY_SECTIONS.filter(s => s.fields.some(f => (form[f.key] ?? "") !== ""))
  const doctorsWithData      = DOCTOR_SECTIONS.filter(s => s.fields.some(f => (form[f.key] ?? "") !== ""))
  const medsWithData         = MED_SECTIONS.filter(s => s.fields.some(f => (form[f.key] ?? "") !== ""))
  const pastPoliciesWithData = PAST_POLICY_SECTIONS.filter(s => s.fields.some(f => (form[f.key] ?? "") !== ""))

  const contactName = [form.firstName, form.lastName].filter(Boolean).join(" ") || "Contact"

  return (
    <div className="cqv-root">
      {devMode && <div className="dev-banner">DEV MODE — changes are not saved to GHL</div>}

      <div className="cqv-header">
        <div className="cqv-header-left">
          <span className="cqv-name">{contactName}</span>
        </div>
        <div className="cqv-header-right">
          <button className="btn-secondary" onClick={() => window.print()}>Print</button>
          <label className={`autosave-toggle${autosave ? " active" : ""}`}>
            <span className="autosave-label">Auto<br/>Save</span>
            <input
              type="checkbox"
              className="autosave-checkbox"
              checked={autosave}
              onChange={e => onAutosaveToggle(e.target.checked)}
            />
          </label>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {error && <div className="cqv-msg error">{error}</div>}
      {saved && <div className="cqv-msg success">Saved</div>}

      <div className="cqv-body">
        {/* Primary + Spouse + Address */}
        <div className="pair-group">
          <div className="pair-grid">
            <SectionCard section={PRIMARY_SECTION} form={form} onChange={handleChange}
              onFieldBlur={autosave ? handleFieldBlur : undefined}
              fieldStates={autosave ? fieldStates : undefined} />
            <SectionCard section={SPOUSE_SECTION}  form={form} onChange={handleChange}
              onFieldBlur={autosave ? handleFieldBlur : undefined}
              fieldStates={autosave ? fieldStates : undefined} />
          </div>
          <div className="address-card-row">
            <SectionCard section={ADDRESS_SECTION} form={form} onChange={handleChange}
              onFieldBlur={autosave ? handleFieldBlur : undefined}
              fieldStates={autosave ? fieldStates : undefined} />
          </div>
        </div>

        {/* Dependents */}
        <GroupAccordion groupKey="deps" emoji={GROUP_EMOJI.deps} title="Dependents"
          open={depsOpen} onToggle={() => setDepsOpen(o => !o)}>
          <div className="card-grid">
            {(showAllDeps ? DEP_SECTIONS : (hasDeps ? depsWithData : [DEP_SECTIONS[0]])).map(s => {
              const sIdx = DEP_SECTIONS.indexOf(s)
              return (
                <SectionCard key={s.id} section={s} form={form} onChange={handleChange}
                  onDelete={() => openDeleteDialog(s.id)}
                  onFieldBlur={autosave ? handleFieldBlur : undefined}
                  fieldStates={autosave ? fieldStates : undefined}
                  dragGroup="dep" dragIndex={sIdx}
                  isDragging={dragState?.group === 'dep' && dragState.from === sIdx}
                  isDragTarget={dragState?.group === 'dep' && dragState.to === sIdx && dragState.from !== sIdx}
                  onGripMouseDown={handleGripMouseDown} />
              )
            })}
          </div>
          <div className="show-all-row">
            <button className="show-all-btn" onClick={(e) => {
              if (!showAllDeps) scrollGroupPanelIntoView(e.currentTarget)
              setShowAllDeps(v => !v)
            }}>
              {showAllDeps ? "Show Less" : "Show All"}
            </button>
          </div>
        </GroupAccordion>

        {/* Active Policies */}
        <GroupAccordion groupKey="active" emoji={GROUP_EMOJI.active} title="Active Policies"
          open={policiesOpen} onToggle={() => setPoliciesOpen(o => !o)}>
          <div className="card-grid">
            {(showAllPolicies ? POLICY_SECTIONS : (hasPolicies ? policiesWithData : [POLICY_SECTIONS[0]])).map(s => {
              const sIdx = POLICY_SECTIONS.indexOf(s)
              return (
                <SectionCard
                  key={s.id} section={s} form={form} onChange={handleChange}
                  onDelete={() => openDeleteDialog(s.id)}
                  onArchive={() => openArchiveDialog(s.id)}
                  onFieldBlur={autosave ? handleFieldBlur : undefined}
                  fieldStates={autosave ? fieldStates : undefined}
                  dragGroup="policy" dragIndex={sIdx}
                  isDragging={dragState?.group === 'policy' && dragState.from === sIdx}
                  isDragTarget={dragState?.group === 'policy' && dragState.to === sIdx && dragState.from !== sIdx}
                  onGripMouseDown={handleGripMouseDown}
                />
              )
            })}
          </div>
          <div className="show-all-row">
            <button className="show-all-btn" onClick={(e) => {
              if (!showAllPolicies) scrollGroupPanelIntoView(e.currentTarget)
              setShowAllPolicies(v => !v)
            }}>
              {showAllPolicies ? "Show Less" : "Show All"}
            </button>
          </div>
        </GroupAccordion>

        {/* Doctors */}
        <GroupAccordion groupKey="doctors" emoji={GROUP_EMOJI.doctors} title="Doctors"
          open={doctorsOpen} onToggle={() => setDoctorsOpen(o => !o)}>
          <AccordionGlobalField
            field={PREFERRED_HOSPITAL_FIELD}
            value={form[PREFERRED_HOSPITAL_FIELD.key] ?? ""}
            onChange={handleChange}
            onBlur={autosave ? handleFieldBlur : undefined}
            saveState={fieldStates[PREFERRED_HOSPITAL_FIELD.key]}
            colorClass="global-hospital"
          />
          <div className="card-grid">
            {(showAllDoctors ? DOCTOR_SECTIONS : (hasDoctors ? doctorsWithData : [DOCTOR_SECTIONS[0]])).map(s => {
              const sIdx = DOCTOR_SECTIONS.indexOf(s)
              return (
                <SectionCard key={s.id} section={s} form={form} onChange={handleChange}
                  onDelete={() => openDeleteDialog(s.id)}
                  onFieldBlur={autosave ? handleFieldBlur : undefined}
                  fieldStates={autosave ? fieldStates : undefined}
                  dragGroup="doc" dragIndex={sIdx}
                  isDragging={dragState?.group === 'doc' && dragState.from === sIdx}
                  isDragTarget={dragState?.group === 'doc' && dragState.to === sIdx && dragState.from !== sIdx}
                  onGripMouseDown={handleGripMouseDown} />
              )
            })}
          </div>
          <div className="show-all-row">
            <button className="show-all-btn" onClick={(e) => {
              if (!showAllDoctors) scrollGroupPanelIntoView(e.currentTarget)
              setShowAllDoctors(v => !v)
            }}>
              {showAllDoctors ? "Show Less" : "Show All"}
            </button>
          </div>
        </GroupAccordion>

        {/* Medications */}
        <GroupAccordion groupKey="medications" emoji={GROUP_EMOJI.medications} title="Medications"
          open={medicationsOpen} onToggle={() => setMedicationsOpen(o => !o)}>
          <AccordionGlobalField
            field={PREFERRED_PHARMACY_FIELD}
            value={form[PREFERRED_PHARMACY_FIELD.key] ?? ""}
            onChange={handleChange}
            onBlur={autosave ? handleFieldBlur : undefined}
            saveState={fieldStates[PREFERRED_PHARMACY_FIELD.key]}
            colorClass="global-pharmacy"
          />
          <div className="card-grid">
            {(showAllMeds ? MED_SECTIONS : (hasMeds ? medsWithData : [MED_SECTIONS[0]])).map(s => {
              const sIdx = MED_SECTIONS.indexOf(s)
              return (
                <SectionCard key={s.id} section={s} form={form} onChange={handleChange}
                  onDelete={() => openDeleteDialog(s.id)}
                  onFieldBlur={autosave ? handleFieldBlur : undefined}
                  fieldStates={autosave ? fieldStates : undefined}
                  dragGroup="med" dragIndex={sIdx}
                  isDragging={dragState?.group === 'med' && dragState.from === sIdx}
                  isDragTarget={dragState?.group === 'med' && dragState.to === sIdx && dragState.from !== sIdx}
                  onGripMouseDown={handleGripMouseDown} />
              )
            })}
          </div>
          <div className="show-all-row">
            <button className="show-all-btn" onClick={(e) => {
              if (!showAllMeds) scrollGroupPanelIntoView(e.currentTarget)
              setShowAllMeds(v => !v)
            }}>
              {showAllMeds ? "Show Less" : "Show All"}
            </button>
          </div>
        </GroupAccordion>

        {/* Past Policies */}
        <GroupAccordion groupKey="past" emoji={GROUP_EMOJI.past} title="Past Policies"
          open={pastPoliciesOpen} onToggle={() => setPastPoliciesOpen(o => !o)}>
          <div className="card-grid">
            {(showAllPast ? PAST_POLICY_SECTIONS : (hasPastPolicies ? pastPoliciesWithData : [PAST_POLICY_SECTIONS[0]])).map(s => {
              const sIdx = PAST_POLICY_SECTIONS.indexOf(s)
              return (
                <SectionCard
                  key={s.id} section={s} form={form} onChange={handleChange}
                  onDelete={() => openDeleteDialog(s.id)}
                  onFieldBlur={autosave ? handleFieldBlur : undefined}
                  fieldStates={autosave ? fieldStates : undefined}
                  dragGroup="past" dragIndex={sIdx}
                  isDragging={dragState?.group === 'past' && dragState.from === sIdx}
                  isDragTarget={dragState?.group === 'past' && dragState.to === sIdx && dragState.from !== sIdx}
                  onGripMouseDown={handleGripMouseDown}
                />
              )
            })}
          </div>
          <div className="show-all-row">
            <button className="show-all-btn" onClick={(e) => {
              if (!showAllPast) scrollGroupPanelIntoView(e.currentTarget)
              setShowAllPast(v => !v)
            }}>
              {showAllPast ? "Show Less" : "Show All"}
            </button>
          </div>
        </GroupAccordion>
      </div>

      {dialogState && (
        <ConfirmDialog
          dialogState={dialogState}
          onCancel={() => setDialogState(null)}
          onConfirm={executeConfirm}
        />
      )}
    </div>
  )
}

// ── Accordion-level global field (centered, single input above card grid) ──
function AccordionGlobalField({
  field, value, onChange, onBlur, saveState, colorClass,
}: {
  field: FieldDef
  value: string
  onChange: (key: string, value: string) => void
  onBlur?: (key: string, value: string) => void
  saveState?: FieldSaveState
  colorClass?: string
}) {
  return (
    <div className={`accordion-global-field${colorClass ? ` ${colorClass}` : ""}`}>
      <label className="accordion-global-label">{field.label}</label>
      <div className="field-input-wrap accordion-global-input-wrap">
        <input
          className="field-input"
          type="text"
          value={value}
          onChange={e => onChange(field.key, e.target.value)}
          onBlur={onBlur ? e => onBlur(field.key, e.target.value) : undefined}
        />
        <CopyButton value={value} />
        {onBlur && <FieldSyncIndicator state={saveState ?? 'idle'} />}
      </div>
    </div>
  )
}

// ── Scroll helper ─────────────────────────────────────────────────────────
function scrollGroupPanelIntoView(btn: HTMLElement) {
  const panel = btn.closest('.group-panel')
  if (!panel) return
  requestAnimationFrame(() => {
    const rect = panel.getBoundingClientRect()
    if (rect.bottom > window.innerHeight) {
      panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  })
}

// ── Group accordion ────────────────────────────────────────────────────────
function GroupAccordion({
  groupKey, emoji, title, open, onToggle, children,
}: {
  groupKey: string; emoji: string; title: string
  open: boolean; onToggle: () => void; children: React.ReactNode
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const prevOpen = useRef(open)

  useEffect(() => {
    if (!prevOpen.current && open && panelRef.current) {
      // After paint, check if the panel bottom is off-screen and scroll it in
      requestAnimationFrame(() => {
        const el = panelRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        const vh = window.innerHeight
        if (rect.bottom > vh) {
          el.scrollIntoView({ behavior: "smooth", block: "nearest" })
        }
      })
    }
    prevOpen.current = open
  }, [open])

  return (
    <div ref={panelRef} className={`group-panel group-${groupKey}`}>
      <button className="group-toggle" onClick={onToggle}>
        <span className="group-emoji">{emoji}</span>
        <span className="group-label">{title}</span>
        <span className={`group-chevron${open ? " open" : ""}`}>›</span>
      </button>
      {open && <div className="group-body">{children}</div>}
    </div>
  )
}

// ── Section card ───────────────────────────────────────────────────────────
function SectionCard({
  section, form, onChange, onDelete, onArchive, onFieldBlur, fieldStates,
  dragGroup, dragIndex, isDragging, isDragTarget, onGripMouseDown,
}: {
  section: SectionDef
  form: Record<string, string>
  onChange: (key: string, value: string) => void
  onDelete?: () => void
  onArchive?: () => void
  onFieldBlur?: (key: string, value: string) => void
  fieldStates?: Record<string, FieldSaveState>
  dragGroup?: string
  dragIndex?: number
  isDragging?: boolean
  isDragTarget?: boolean
  onGripMouseDown?: (e: React.MouseEvent, group: string, idx: number) => void
}) {
  const cardType      = getCardType(section.id)
  const emoji         = CARD_EMOJI[cardType]
  const genderField         = section.fields.find(f => f.isGender)
  const preferredNameField  = section.fields.find(f => f.isPreferredName)
  const regularFields       = section.fields.filter(f => !f.isGender && !f.isPreferredName)

  let headerAction: React.ReactNode = null
  if (cardType === "policy" && onDelete && onArchive) {
    headerAction = <PolicyActionButton onArchive={onArchive} onDelete={onDelete} />
  } else if (cardType === "past" && onDelete) {
    headerAction = <PastDeleteButton onDelete={onDelete} />
  } else if ((cardType === "doctor" || cardType === "medication") && onDelete) {
    headerAction = <PastDeleteButton onDelete={onDelete} />
  } else if (cardType === "dep" && onDelete) {
    headerAction = (
      <>
        {genderField && (
          <GenderToggle value={form[genderField.key] ?? ""} fieldKey={genderField.key} onChange={onChange} />
        )}
        <PastDeleteButton onDelete={onDelete} />
      </>
    )
  } else if (genderField) {
    headerAction = (
      <GenderToggle value={form[genderField.key] ?? ""} fieldKey={genderField.key} onChange={onChange} />
    )
  }

  const draggable = dragGroup != null && dragIndex != null

  return (
    <div
      className={`cqv-card${isDragging ? ' card-dragging' : ''}${isDragTarget ? ' card-drag-target' : ''}`}
      data-drag-group={dragGroup}
      data-drag-idx={dragIndex != null ? String(dragIndex) : undefined}
    >
      <div className={`card-hdr hdr-${cardType}`}>
        {draggable && (
          <span
            className="card-drag-handle"
            onMouseDown={e => onGripMouseDown?.(e, dragGroup!, dragIndex!)}
          >
            <svg width="10" height="14" viewBox="0 0 10 14" fill="none">
              <circle cx="3" cy="2.5"  r="1.3" fill="currentColor"/>
              <circle cx="3" cy="7"    r="1.3" fill="currentColor"/>
              <circle cx="3" cy="11.5" r="1.3" fill="currentColor"/>
              <circle cx="7" cy="2.5"  r="1.3" fill="currentColor"/>
              <circle cx="7" cy="7"    r="1.3" fill="currentColor"/>
              <circle cx="7" cy="11.5" r="1.3" fill="currentColor"/>
            </svg>
          </span>
        )}
        <span className="card-hdr-emoji">{emoji}</span>
        <span className="card-hdr-label">{section.label}</span>
        {preferredNameField && (
          <div className="hdr-pref-name">
            <input
              className="hdr-pref-name-input"
              type="text"
              placeholder="Preferred Name"
              value={form[preferredNameField.key] ?? ""}
              onChange={e => onChange(preferredNameField.key, e.target.value)}
              onBlur={onFieldBlur ? e => onFieldBlur(preferredNameField.key, e.target.value) : undefined}
            />
          </div>
        )}
        {headerAction}
      </div>
      <div className="field-grid">
        {regularFields.map(field => (
          <FieldInput
            key={field.key}
            field={field}
            value={form[field.key] ?? ""}
            onChange={onChange}
            onBlur={onFieldBlur}
            saveState={fieldStates?.[field.key]}
          />
        ))}
      </div>
    </div>
  )
}

// ── Policy action button (Archive/Delete dropdown) ─────────────────────────
function PolicyActionButton({ onArchive, onDelete }: { onArchive: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="policy-action-wrap">
      <button
        className="policy-action-btn"
        onClick={() => setOpen(o => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
      >
        Archive/Delete
        <span className={`policy-caret${open ? " open" : ""}`}>›</span>
      </button>
      {open && (
        <div className="policy-menu">
          <button className="policy-opt"
            onMouseDown={e => { e.preventDefault(); onArchive(); setOpen(false) }}>
            Archive
          </button>
          <button className="policy-opt policy-opt-del"
            onMouseDown={e => { e.preventDefault(); onDelete(); setOpen(false) }}>
            Delete
          </button>
        </div>
      )}
    </div>
  )
}

// ── Past policy delete button ─────────────────────────────────────────────
function PastDeleteButton({ onDelete }: { onDelete: () => void }) {
  return (
    <button className="past-delete-btn" onClick={onDelete}>
      Delete
    </button>
  )
}

// ── Confirmation dialog ────────────────────────────────────────────────────
function ConfirmDialog({
  dialogState, onCancel, onConfirm,
}: {
  dialogState: PolicyDialogState
  onCancel: () => void
  onConfirm: (cancelDate: string) => void
}) {
  const isDelete = dialogState.type === "delete"
  const today = new Date().toISOString().slice(0, 10)
  const [cancelDate, setCancelDate] = useState(today)
  const [isNA, setIsNA] = useState(false)

  const canArchive = isNA || cancelDate !== ""
  const resolvedDate = isNA ? "N/A" : cancelDate

  return (
    <div className="dialog-overlay" onClick={e => { if (e.target === e.currentTarget) onCancel() }}>
      <div className="dialog-card">
        <div className="dialog-header">
          {isDelete ? `Delete ${dialogState.label}?` : `Archive ${dialogState.label}?`}
        </div>
        <div className="dialog-body">
          {!isDelete && (
            <p className="dialog-note">This policy will be moved to Past Policies.</p>
          )}
          <div className="dialog-summary">
            {dialogState.summary.map(([k, v]) => (
              <div key={k} className="dialog-summary-row">
                <span className="dialog-summary-key">{k}</span>
                <span className="dialog-summary-val">{v}</span>
              </div>
            ))}
          </div>
          {!isDelete && (
            <div className="cancel-date-section">
              <p className="cancel-date-required">Enter cancellation date or N/A before archiving.</p>
              <label className="cancel-date-label">Cancellation Date</label>
              <div className="cancel-date-row">
                <input
                  type="date"
                  className={`field-input cancel-date-input${isNA ? " cancel-date-muted" : ""}`}
                  value={cancelDate}
                  onChange={e => { setCancelDate(e.target.value); setIsNA(false) }}
                  onFocus={() => setIsNA(false)}
                />
                <button
                  className={`cancel-na-btn${isNA ? " active" : ""}`}
                  onClick={() => { setIsNA(v => !v); setCancelDate("") }}
                >
                  N/A
                </button>
              </div>
            </div>
          )}
          {isDelete && <p className="dialog-warn">This action cannot be undone.</p>}
        </div>
        <div className="dialog-footer">
          <button className="btn-secondary" onClick={onCancel}>Cancel</button>
          {isDelete
            ? <button className="btn-danger" onClick={() => onConfirm("")}>Delete Policy</button>
            : <button className="btn-primary" onClick={() => onConfirm(resolvedDate)} disabled={!canArchive}>
                Archive Policy
              </button>
          }
        </div>
      </div>
    </div>
  )
}

// ── Gender toggle ──────────────────────────────────────────────────────────
function GenderToggle({
  value, fieldKey, onChange,
}: {
  value: string; fieldKey: string; onChange: (key: string, value: string) => void
}) {
  const [open, setOpen] = useState(false)
  const display = value === "Male" || value === "Female" ? value : "Gender"
  return (
    <div className="gender-wrap">
      <button
        className={`gender-btn${value ? " has-value" : ""}`}
        onClick={() => setOpen(o => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
      >
        {display}
        <span className={`gender-caret${open ? " open" : ""}`}>›</span>
      </button>
      {open && (
        <div className="gender-menu">
          {["Male", "Female"].map(opt => (
            <button key={opt} className={`gender-opt${value === opt ? " sel" : ""}`}
              onMouseDown={e => { e.preventDefault(); onChange(fieldKey, opt); setOpen(false) }}>
              {opt}
            </button>
          ))}
          <button className="gender-opt gender-clear"
            onMouseDown={e => { e.preventDefault(); onChange(fieldKey, ""); setOpen(false) }}>
            Clear
          </button>
        </div>
      )}
    </div>
  )
}

// ── Field sync indicator ───────────────────────────────────────────────────
const SYNC_CIRCUMFERENCE = 2 * Math.PI * 5.5  // r=5.5 → ≈34.56

function FieldSyncIndicator({ state }: { state: FieldSaveState }) {
  if (state === 'saving') {
    return (
      <svg className="field-sync-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="5.5" stroke="#e5e7eb" strokeWidth="1.5"/>
        <circle cx="8" cy="8" r="5.5" stroke="#059669" strokeWidth="1.5"
          strokeLinecap="round"
          strokeDasharray={SYNC_CIRCUMFERENCE}
          strokeDashoffset={SYNC_CIRCUMFERENCE}
          className="field-sync-arc"/>
      </svg>
    )
  }
  if (state === 'saved') {
    return (
      <svg className="field-sync-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="5.5" stroke="#059669" strokeWidth="1.5"/>
        <path d="M5 8.5L7 10.5L11 6" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  }
  return null
}

// ── Copy button ────────────────────────────────────────────────────────────
function CopyButton({ value }: { value: string }) {
  const [toastPos, setToastPos] = useState<{ x: number; y: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)

  function handleCopy() {
    if (!value) return
    navigator.clipboard.writeText(value).then(() => {
      if (btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect()
        setToastPos({ x: rect.left + rect.width / 2, y: rect.top })
      }
      setTimeout(() => setToastPos(null), 1500)
    }).catch(() => {})
  }

  return (
    <>
      <button ref={btnRef} className="copy-btn" type="button" onClick={handleCopy} tabIndex={-1} aria-label="Copy">
        <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <rect x="1" y="3.5" width="8" height="9" rx="1.2" stroke="currentColor" strokeWidth="1.3"/>
          <rect x="5" y="1" width="8" height="9" rx="1.2" stroke="currentColor" strokeWidth="1.3"/>
        </svg>
      </button>
      {toastPos && (
        <div className="copy-toast" style={{ position: 'fixed', left: toastPos.x, top: toastPos.y - 4, transform: 'translate(-50%, -100%)', zIndex: 9999 }}>
          Copied!
        </div>
      )}
    </>
  )
}

// ── Select caret ───────────────────────────────────────────────────────────
function SelectCaret() {
  return (
    <svg className="select-caret-icon" viewBox="0 0 10 6" fill="none" aria-hidden="true">
      <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

// ── Field input ────────────────────────────────────────────────────────────
function FieldInput({
  field, value, onChange, onBlur, saveState,
}: {
  field: FieldDef; value: string; onChange: (key: string, value: string) => void
  onBlur?: (key: string, value: string) => void
  saveState?: FieldSaveState
}) {
  const spanCols  = field.span !== undefined ? field.span : (field.half ? 6 : 12)
  const gridColumn = field.rowStart ? `1 / span ${spanCols}` : `span ${spanCols}`

  const inputType =
    field.type === "date"     ? "date"   :
    field.type === "email"    ? "email"  :
    field.type === "tel"      ? "tel"    :
    field.type === "number"   ? "number" :
    field.type === "currency" ? "text"   : "text"

  return (
    <div className="field-wrap" style={{ gridColumn }}>
      <label className="field-label">{field.label}</label>
      <div className="field-input-wrap">
        {field.type === "yesno" ? (
          <>
            <YesNoSelect
              value={value}
              onChange={v => onChange(field.key, v)}
              onBlur={onBlur ? v => onBlur(field.key, v) : undefined}
            />
            <CopyButton value={value} />
            <SelectCaret />
          </>
        ) : field.type === "select" ? (
          <>
            <select
              className="field-select field-select-custom"
              value={value}
              onChange={e => onChange(field.key, e.target.value)}
              onBlur={onBlur ? e => onBlur(field.key, e.target.value) : undefined}
            >
              <option value="">—</option>
              {(field.options ?? []).map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <CopyButton value={value} />
            <SelectCaret />
          </>
        ) : (
          <>
            <input
              className="field-input"
              type={inputType}
              value={value}
              placeholder={field.type === "currency" ? "$0.00" : ""}
              onChange={e => onChange(field.key, e.target.value)}
              onBlur={onBlur ? e => onBlur(field.key, e.target.value) : undefined}
            />
            <CopyButton value={value} />
          </>
        )}
        {onBlur && <FieldSyncIndicator state={saveState ?? 'idle'} />}
      </div>
    </div>
  )
}

// ── Yes / No select ────────────────────────────────────────────────────────
function YesNoSelect({ value, onChange, onBlur }: {
  value: string; onChange: (v: string) => void; onBlur?: (v: string) => void
}) {
  return (
    <select className="field-select field-select-custom" value={value}
      onChange={e => onChange(e.target.value)}
      onBlur={onBlur ? e => onBlur(e.target.value) : undefined}
    >
      <option value="">—</option>
      <option value="Yes">Yes</option>
      <option value="No">No</option>
    </select>
  )
}
